import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { client } from "@/hono";

/**
 * The signed URL of an article's picture, or `null` when it carries none.
 *
 * Two calls, as on native, because that is what the files API offers: the collection read lists
 * an article's linked files and the single-file read is what signs one. An article carries at most
 * one picture — `patchArticle` deletes the others when a new upload arrives — so the newest link
 * is it. A signed URL lives an hour, so the query goes stale well inside that.
 *
 * @param articleId - The article whose picture to resolve.
 */
export function useArticleImage(articleId: string) {
	/** Query */
	const query = useQuery({
		queryKey: ["article-image", articleId],
		queryFn: async () => {
			const filesResponse = await client.files[":tableName"][":key"][":identifier"].$get({
				param: { tableName: "articleFiles", key: "articleId", identifier: articleId },
			});
			const { files } = await responseHandler(filesResponse);
			const fileId = files.at(0)?.id;
			if (!fileId) return null;

			// A file whose signed URL cannot be resolved answers `success: false`, which
			// `responseHandler` throws on. A missing picture is not worth a toast, so it is
			// suppressed and read as no picture.
			const urlResponse = await client.files[":fileId"].$get({ param: { fileId } });
			const { signedUrl } = await responseHandler(urlResponse, false);
			return signedUrl ?? null;
		},
		staleTime: 1000 * 60 * 30,
	});

	/** Return */
	return { ...query, imageUrl: query.data ?? null };
}
